import { useMqttSubscription } from '@hooks/mqtt/useMqttSubscription';
import { GridApiPro } from '@mui/x-data-grid-pro';
import { DeviceDataTableRow } from '@src/components/_shared/DeviceDataTable/types';
import { DataPointWithValue, isDataPointWithValue } from '@src/types/DataPoint';
import { getFieldNameFromDataPoint } from '@src/utils/gridUtils';
import type { MqttMessage } from '@store/atoms/mqtt/types';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

interface TopicInfo {
  rowId: string;
  fieldName: string;
  dataPoint: DataPointWithValue;
}

type TopicToRowMap = Map<string, TopicInfo>;

function buildTopicToRowMap(rows: DeviceDataTableRow[]): TopicToRowMap {
  const map = new Map<string, TopicInfo>();

  rows.forEach((row) => {
    Object.values(row).forEach((cellValue) => {
      if (isDataPointWithValue(cellValue) && cellValue.fullTopic) {
        const fieldName = getFieldNameFromDataPoint(cellValue);
        map.set(cellValue.fullTopic, {
          rowId: row.id,
          fieldName,
          dataPoint: cellValue,
        });
      }
    });
  });

  return map;
}

export function useGridMqttSubscription(
  rows: DeviceDataTableRow[],
  apiRef: MutableRefObject<GridApiPro>,
): void {
  const prevValueRef = useRef(new Map<string, unknown>());

  const topicToRowMap = useMemo(() => buildTopicToRowMap(rows), [rows]);

  const allTopics = useMemo(
    () => Array.from(topicToRowMap.keys()),
    [topicToRowMap],
  );

  useEffect(() => {
    const currentTopics = new Set(allTopics);
    prevValueRef.current.forEach((_, key) => {
      if (!currentTopics.has(key)) {
        prevValueRef.current.delete(key);
      }
    });
  }, [allTopics]);

  const handleMessage = useCallback(
    (topic: string, message: MqttMessage) => {
      const info = topicToRowMap.get(topic);
      if (!info || message?.value === undefined) {
        return;
      }

      const prevValue = prevValueRef.current.get(topic);
      if (message.value === prevValue) {
        return;
      }
      prevValueRef.current.set(topic, message.value);

      apiRef.current?.updateRows([
        {
          id: info.rowId,
          [info.fieldName]: {
            ...info.dataPoint,
            value: message.value,
          },
        },
      ]);
    },
    [topicToRowMap, apiRef],
  );

  useMqttSubscription(allTopics, {
    enabled: allTopics.length > 0,
    onMessage: handleMessage,
  });
}
